<template>
    <!-- Desktop Modal -->
    <UiModal
        v-if="!isMobile"
        :is-open="modelValue"
        @close="close"
        width="560px"
        :no-padding="true"
        modal-class="overflow-hidden"
    >
        <template #title>
            <span></span>
        </template>

        <template #close>
            <span></span>
        </template>

        <template #content>
            <div>
                <!-- Image with background and gradient overlay -->
                <div class="relative bg-slate-100 dark:bg-slate-700 overflow-hidden px-6 pt-4">
                    <!-- Close button -->
                    <div class="flex justify-end">
                        <button
                            type="button"
                            class="w-10 h-10 flex items-center justify-center rounded-lg focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200"
                            @click="close"
                        >
                            <Icon
                                icon="x"
                                path-class="stroke-slate-500 hover:stroke-slate-700"
                                svg-class="fill-slate-500 hover:fill-slate-700 h-5 w-5"
                            />
                        </button>
                    </div>
                    <img
                        :src="bestCardsImage"
                        alt="Best cards by category preview"
                        class="w-full"
                    />
                    <div
                        class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-100 to-transparent pointer-events-none"
                    ></div>
                </div>

                <!-- Content -->
                <div class="mt-6 px-6">
                    <h2 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
                        Best credit cards to use
                    </h2>
                    <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                        Automatically shows which of your credit cards is the
                        best choice for each spending category.
                    </p>

                    <ul class="mt-4 space-y-3">
                        <li class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-red-500 shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-slate-700 dark:text-slate-200">
                                Always use the right card
                            </span>
                        </li>
                        <li class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-red-500 shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-slate-700 dark:text-slate-200">
                                Maximize rewards on every purchase
                            </span>
                        </li>
                        <li class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-red-500 shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-slate-700 dark:text-slate-200">
                                No need to compare benefits manually
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex items-center justify-end gap-3 px-6 pb-4">
                <button
                    type="button"
                    class="px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                    @click="close"
                >
                    Close
                </button>
                <Link
                    href="/wallet/best-cards"
                    class="px-4 py-2.5 text-sm font-medium text-white bg-10x-orange rounded shadow-sm hover:bg-orange-600 transition-colors"
                >
                    Explore Best cards by category
                </Link>
            </div>
        </template>
    </UiModal>

    <!-- Mobile Modal -->
    <UiModalMobile
        v-else
        :is-open="modelValue"
        title="Best credit cards"
        @close="close"
    >
        <template #content>
            <div>
                <!-- Image with background and gradient overlay -->
                <div class="relative bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                    <img
                        :src="bestCardsImage"
                        alt="Best cards by category preview"
                        class="w-full"
                    />
                    <div
                        class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-100 to-transparent pointer-events-none"
                    ></div>
                </div>

                <!-- Content -->
                <div class="mt-6">
                    <h2 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
                        Best credit cards to use
                    </h2>
                    <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                        Automatically shows which of your credit cards is the
                        best choice for each spending category.
                    </p>

                    <ul class="mt-4 space-y-3">
                        <li class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-red-500 shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-slate-700 dark:text-slate-200">
                                Always use the right card
                            </span>
                        </li>
                        <li class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-red-500 shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-slate-700 dark:text-slate-200">
                                Maximize rewards on every purchase
                            </span>
                        </li>
                        <li class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-red-500 shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-slate-700 dark:text-slate-200">
                                No need to compare benefits manually
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex items-center justify-end gap-3">
                <button
                    type="button"
                    class="px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                    @click="close"
                >
                    Close
                </button>
                <Link
                    href="/wallet/best-cards"
                    class="px-4 py-2.5 text-sm font-medium text-white bg-10x-orange rounded shadow-sm hover:bg-orange-600 transition-colors"
                >
                    Explore Best cards by category
                </Link>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import { Link } from '@inertiajs/vue3';
import { useIsMobile } from '@/composables/useIsMobile';
import bestCardsImage from '../../../../images/best-cards-learn-more-modal.png';

const { isMobile } = useIsMobile();

defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const close = () => {
    emit('update:modelValue', false);
};
</script>
